import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Loader2, X } from 'lucide-react';
import { cn } from '@seller-hermes/utils/cn';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

/**
 * ⚠️ PADRÃO DO ui-v2 — camada FECHADA não pode engolir clique.
 *
 * O Radix desmonta o conteúdo no `animationend`. Quando esse evento não chega
 * (medido em Chrome: acontece), o nó fica montado, invisível, e continua
 * recebendo ponteiro — o sintoma é "o botão não funciona", às vezes sem nem
 * acender o hover, porque o gatilho fica DEBAIXO do fantasma do próprio
 * popover que ele abre.
 *
 * Dois tratamentos, e a escolha depende de quem bloqueia:
 *  - camada em POPPER (menu, popover, select, tooltip, hovercard, menu de
 *    contexto): além do guard, a animação de SAÍDA sai. O `[data-radix-popper-
 *    content-wrapper]` é PAI do conteúdo e não recebe classe nossa — só a
 *    desmontagem imediata tira ele do caminho;
 *  - camada em OVERLAY (dialog, alert, sheet, toast): o próprio elemento é o
 *    que cobre a tela, então o guard basta e a animação de saída fica (num
 *    modal ela é bem mais percebida que num menu).
 *
 * O `!` é obrigatório: o Radix escreve `pointer-events` INLINE ao fechar, e
 * estilo inline ganha de classe.
 */
const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    onClick={(e) => {
      e.stopPropagation();
    }}
    className={cn(
      'bg-background/80 fixed inset-0 z-50 grid place-items-center overflow-hidden backdrop-blur-sm',
      'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:!pointer-events-none',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    removeCloseIcon?: boolean;
    isLoading?: boolean;
    overlayClassName?: string;
  }
>(
  (
    {
      className,
      overlayClassName,
      children,
      removeCloseIcon = false,
      isLoading = false,
      ...props
    },
    ref,
  ) => (
    <DialogPortal>
      <DialogOverlay className={overlayClassName}>
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            'bg-background fixed z-50 grid max-h-[90%] w-full max-w-lg gap-y-4 overflow-y-auto border p-6 shadow-lg duration-200',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=closed]:!pointer-events-none',
            'sm:rounded-lg md:w-full',
            'focus:ring-ring focus:outline-none focus:ring-2 focus:ring-offset-2',
            className,
          )}
          {...props}
        >
          {isLoading ? (
            <div className="flex h-full w-full items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            children
          )}
          {!removeCloseIcon && (
            <DialogPrimitive.Close className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute right-4 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogOverlay>
    </DialogPortal>
  ),
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col space-y-1.5 text-center sm:text-left',
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-none tracking-tight',
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-muted-foreground text-sm', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export default Object.assign(Dialog, {
  Trigger: DialogTrigger,
  Content: DialogContent,
  Close: DialogClose,
  Header: DialogHeader,
  Footer: DialogFooter,
  Title: DialogTitle,
  Description: DialogDescription,
});
